import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'
import {
  borderRadius,
  breakpoint,
  fontSize,
  pxToRem,
  size,
  width,
} from '@twistopayments/design-system/styles'

export const section = css`
  padding-bottom: ${size[24]};
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto ${size[32]};

  ${breakpoint.up('smMd')} {
    padding-bottom: 0;
  }
`

export const container = css`
  padding: ${size[24]} ${size[24]} 0;

  ${breakpoint.up('smMd')} {
    max-width: ${width.container.wide};
    padding: ${size[24]};

    display: flex;
    flex-direction: column;
    align-items: center;
  }
`

export const title = css`
  font-size: ${pxToRem(32)};
  line-height: 1.2;
  max-width: ${width.modal.compact};
  margin-top: ${size[24]};

  ${breakpoint.up('smMd')} {
    font-size: ${pxToRem(48)};
    text-align: center;
  }
`

export const highlighted = (theme: Theme) => css`
  color: ${theme.palette.secondary.mainContrast};
`

export const description = css`
  margin-top: ${size[24]};
  font-size: ${fontSize.md};
  line-height: ${size[24]};

  ${breakpoint.up('smMd')} {
    margin-top: ${size[40]};
    text-align: center;
  }
`

export const items = css`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(290px, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: ${size[16]};
  padding: ${size[24]};
  width: 100%;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-color: transparent transparent;

  ${breakpoint.up('smMd')} {
    overflow-x: visible;
    grid-template-columns: repeat(3, minmax(0, 450px));
    gap: ${size[24]};
    width: fit-content;
  }
`

export const item = (theme: Theme) => css`
  position: relative;
  border-radius: ${theme.borderRadius.sm};
  min-height: 314px;

  img {
    border-radius: ${theme.borderRadius.sm};
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    transition: background-color 0.3s ease-out;
    background: linear-gradient(
      270.01deg,
      rgba(32, 22, 43, 0.052) 33.04%,
      rgba(69, 13, 135, 0.52) 99.99%
    );
  }

  ${breakpoint.up('smMd')} {
    min-height: 350px;
  }
`

export const hover = css`
  &:hover {
    cursor: pointer;

    &::after {
      background-color: rgba(69, 13, 135, 0.8);
      transition: background-color 0.2s ease-in;
    }

    .gallery-item-content {
      display: flex;
      flex-direction: column;
    }
  }
`

export const content = (theme: Theme) => css`
  position: absolute;
  display: none;
  padding: ${size[24]};
  bottom: 0;
  z-index: 1;
  color: ${theme.palette.text.titleReversed};
  width: 100%;

  a {
    width: 100%;
    max-height: ${size[36]};
  }

  ${breakpoint.up('smMd')} {
    padding: ${size[32]};

    a {
      width: fit-content;
      max-height: unset;
    }
  }
`

export const itemSubtitle = css`
  font-size: ${fontSize.sm};
  margin: 0 0 ${size[8]};
`

export const itemTitle = css`
  font-size: ${fontSize.md};
  margin: 0 0 ${size[24]};
`

export const sliderButton = (theme: Theme) => css`
  position: absolute;
  top: 50%;
  left: -${size[8]};
  width: ${size[60]};
  height: ${size[60]};
  transform: translateY(-50%);
  z-index: 1;
  background-color: ${theme.palette.background.main};
  border: none;
  border-radius: ${borderRadius.circle};
  box-shadow: ${theme.shadows.defaultActive};
  cursor: pointer;

  :hover {
    background-color: ${theme.palette.background.accent};
  }

  svg {
    width: ${size[24]};
    height: ${size[24]};
  }
`

export const next = css`
  left: initial;
  right: -${size[8]};

  svg {
    transform: rotate(180deg);
  }
`
